import { useEffect, useRef, useState } from 'react';
import { captureWorkbookContext, invalidateSnapshotCache, isInOffice } from '@/services/office';
import type { WorkbookContext } from '@/types/chat';

/**
 * Polls for active-cell/selection changes. Excel's
 * `SelectionChanged` event is per-worksheet, so rather than managing
 * add/remove across sheet switches we use a light poll (250ms) which
 * is plenty fast for UI purposes.
 *
 * Side effect: when the active cell or selection changes, the
 * snapshot cache is invalidated so the next chat send captures a
 * fresh snapshot. Without this, a user could move cursor + send
 * within 5 sec and the agent would see the stale active cell.
 */
export function useActiveCell(): WorkbookContext {
  const [ctx, setCtx] = useState<WorkbookContext>({});
  const lastKey = useRef<string>('');

  useEffect(() => {
    if (!isInOffice()) return;
    let cancelled = false;

    const tick = async () => {
      let latest: WorkbookContext;
      try {
        latest = await captureWorkbookContext();
      } catch {
        // Defense-in-depth: the poll should already swallow its own errors,
        // but never let a transient tick failure become an uncaught
        // rejection. Keep the last context until the next tick.
        return;
      }
      if (cancelled) return;
      // Drop snapshot cache when active cell or selection moved — the
      // memo would otherwise serve stale "Active: ..." prose to the
      // backend.
      const key = `${latest.active_sheet ?? ''}|${latest.active_cell ?? ''}|${latest.selected_range ?? ''}`;
      if (key !== lastKey.current) {
        if (lastKey.current !== '') invalidateSnapshotCache();
        lastKey.current = key;
      }
      setCtx(latest);
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  return ctx;
}
